<template>
  <div class="date-picker-container">
    <!-- Input Trigger -->
    <div 
      class="date-input-trigger" 
      :class="{ 'focused': isOpen, 'has-value': modelValue }"
      @click="toggleCalendar"
    >
      <span class="selected-date">
        {{ displayValue }}
      </span>
      <svg class="calendar-icon" width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M8 2V5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M16 2V5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M3 9H21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        <rect x="3" y="4" width="18" height="17" rx="4" stroke="currentColor" stroke-width="1.5"/>
        <path d="M12 14H12.009" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M16 14H16.009" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M8 14H8.009" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M12 18H12.009" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M16 18H16.009" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M8 18H8.009" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </div>

    <!-- Calendar Popup -->
    <transition name="calendar-slide">
      <div v-if="isOpen" class="calendar-popup">
        <div class="calendar-header">
          <button type="button" class="nav-button" @click="previousMonth" aria-label="Previous month">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M15 6L9 12L15 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          
          <div class="month-year-display">
            <select class="month-select" :value="currentMonth" @change="handleMonthChange">
              <option v-for="(m, index) in monthsList" :key="index" :value="index">{{ m }}</option>
            </select>
            <select class="year-select" :value="currentYear" @change="handleYearChange">
              <option v-for="y in yearList" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
          
          <button type="button" class="nav-button" @click="nextMonth" aria-label="Next month">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M9 6L15 12L9 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>

        <div class="week-days">
          <div v-for="day in weekDays" :key="day" class="week-day">
            {{ day }}
          </div>
        </div>

        <div class="calendar-grid">
          <div 
            v-for="day in calendarDays" 
            :key="day.date"
            class="calendar-day"
            :class="{
              'current-month': day.isCurrentMonth,
              'selected': isSelected(day.date),
              'today': isToday(day.date),
              'disabled': isDateDisabled(day)
            }"
            @click="selectDate(day.date)"
          >
            {{ day.day }}
          </div>
        </div>

        <!-- Time Selectors (if enabled) -->
        <div v-if="enableTime" class="time-selectors">
          <div class="time-selector-group">
            <label>Hour</label>
            <select v-model="selectedHour" class="time-select">
              <option v-for="h in 24" :key="h-1" :value="h-1">{{ String(h-1).padStart(2, '0') }}</option>
            </select>
          </div>
          <div class="time-separator">:</div>
          <div class="time-selector-group">
            <label>Minute</label>
            <select v-model="selectedMinute" class="time-select">
              <option v-for="m in 60" :key="m-1" :value="m-1">{{ String(m-1).padStart(2, '0') }}</option>
            </select>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="calendar-actions">
          <button type="button" class="action-btn clear" @click="clearDate" v-if="modelValue">
            Clear
          </button>
          <button type="button" class="action-btn today" @click="selectToday" v-if="!enableTime">
            Today
          </button>
          <button type="button" class="action-btn apply" @click="closeCalendar" v-if="enableTime">
            Apply
          </button>
        </div>
      </div>
    </transition>

    <!-- Backdrop -->
    <transition name="fade">
      <div v-if="isOpen" class="calendar-backdrop" @click="closeCalendar"></div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';

export default {
  name: 'DatePicker',
  props: {
    modelValue: {
      type: [String, Date],
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Select date'
    },
    min: {
      type: String,
      default: ''
    },
    max: {
      type: String,
      default: ''
    },
    enableTime: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  
  setup(props, { emit }) {
    const isOpen = ref(false);
    const currentDate = ref(new Date());
    const selectedDate = ref(null);

    // Week days
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const selectedHour = ref(0);
    const selectedMinute = ref(0);

    // Helper to get formatted string from value
    const getModelString = (val) => {
        if (!val) return '';
        if (val instanceof Date) {
            const yyyy = val.getFullYear();
            const mm = String(val.getMonth() + 1).padStart(2, '0');
            const dd = String(val.getDate()).padStart(2, '0');
            const HH = String(val.getHours()).padStart(2, '0');
            const min = String(val.getMinutes()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd} ${HH}:${min}`;
        }
        return String(val);
    };

    // Computed properties
    const displayValue = computed(() => {
      if (!props.modelValue) return props.placeholder;
      
      const valStr = getModelString(props.modelValue);
      // Replace space with T for reliable parsing across browsers
      const parsedDate = new Date(valStr.replace(' ', 'T'));
      
      const dateStr = parsedDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      
      if (props.enableTime) {
        const hh = String(parsedDate.getHours()).padStart(2, '0');
        const mm = String(parsedDate.getMinutes()).padStart(2, '0');
        return `${dateStr} ${hh}:${mm}`;
      }
      return dateStr;
    });

    const currentYear = computed(() => currentDate.value.getFullYear());
    const currentMonth = computed(() => currentDate.value.getMonth());

    const monthsList = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];

    const yearList = computed(() => {
      const years = [];
      const currentY = new Date().getFullYear();
      for (let i = currentY - 100; i <= currentY + 10; i++) {
        years.push(i);
      }
      return years;
    });

    const handleMonthChange = (e) => {
      const d = new Date(currentDate.value);
      d.setMonth(parseInt(e.target.value));
      currentDate.value = d;
    };

    const handleYearChange = (e) => {
      const d = new Date(currentDate.value);
      d.setFullYear(parseInt(e.target.value));
      currentDate.value = d;
    };

    // Calendar days generation
    const calendarDays = computed(() => {
      const year = currentYear.value;
      const month = currentMonth.value;
      
      // First day of the month
      const firstDay = new Date(year, month, 1);
      // Last day of the month
      const lastDay = new Date(year, month + 1, 0);
      
      // Days from previous month
      const prevMonthDays = firstDay.getDay();
      // Days from next month
      const nextMonthDays = 6 - lastDay.getDay();
      
      const days = [];
      
      // Helper function to format date as YYYY-MM-DD using local timezone
      const formatLocalDate = (d) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      };
      
      // Previous month days
      for (let i = prevMonthDays - 1; i >= 0; i--) {
        const date = new Date(year, month, -i);
        days.push({
          day: date.getDate(),
          date: formatLocalDate(date),
          isCurrentMonth: false
        });
      }
      
      // Current month days
      for (let i = 1; i <= lastDay.getDate(); i++) {
        const date = new Date(year, month, i);
        days.push({
          day: i,
          date: formatLocalDate(date),
          isCurrentMonth: true
        });
      }
      
      // Next month days
      for (let i = 1; i <= nextMonthDays; i++) {
        const date = new Date(year, month + 1, i);
        days.push({
          day: i,
          date: formatLocalDate(date),
          isCurrentMonth: false
        });
      }
      
      return days;
    });

    // Methods
    const toggleCalendar = () => {
      if (!isOpen.value) {
        if (props.modelValue) {
          const valStr = getModelString(props.modelValue);
          const parsedDate = new Date(valStr.replace(' ', 'T'));
          if (!isNaN(parsedDate.getTime())) {
            currentDate.value = parsedDate;
          }
        } else {
          currentDate.value = new Date();
        }
      }
      isOpen.value = !isOpen.value;
    };

    const closeCalendar = () => {
      isOpen.value = false;
    };

    const emitDate = (dateStr) => {
      let finalVal = dateStr;
      if (props.enableTime) {
        const hh = String(selectedHour.value).padStart(2, '0');
        const mm = String(selectedMinute.value).padStart(2, '0');
        finalVal = `${dateStr} ${hh}:${mm}`;
      }
      emit('update:modelValue', finalVal);
    };

    const selectDate = (date) => {
      // Check min/max validation
      if (props.min && date < props.min) return;
      if (props.max && date > props.max) return;
      
      emitDate(date);
      selectedDate.value = date;
      
      if (!props.enableTime) {
        closeCalendar();
      }
    };

    watch([selectedHour, selectedMinute], () => {
      if (selectedDate.value) {
        emitDate(selectedDate.value);
      }
    });

    const clearDate = () => {
      emit('update:modelValue', '');
      selectedDate.value = null;
    };

    const selectToday = () => {
      const today = new Date();
      currentDate.value = new Date(today);
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      selectDate(`${yyyy}-${mm}-${dd}`);
    };

    const previousMonth = () => {
      currentDate.value = new Date(currentDate.value.setMonth(currentDate.value.getMonth() - 1));
    };

    const nextMonth = () => {
      currentDate.value = new Date(currentDate.value.setMonth(currentDate.value.getMonth() + 1));
    };

    const isSelected = (date) => {
      const valStr = getModelString(props.modelValue);
      return valStr.startsWith(date);
    };

    const isToday = (date) => {
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const dd = String(today.getDate()).padStart(2, '0');
      return date === `${yyyy}-${mm}-${dd}`;
    };

    const isDateDisabled = (day) => {
      if (!day.isCurrentMonth) return true;
      if (props.min && day.date < props.min) return true;
      if (props.max && day.date > props.max) return true;
      return false;
    };

    // Close calendar when clicking outside
    const handleClickOutside = (event) => {
      const datePicker = event.target.closest('.date-picker-container');
      if (!datePicker && isOpen.value) {
        closeCalendar();
      }
    };

    // Set current date to selected date when modelValue changes
    watch(() => props.modelValue, (newValue) => {
      if (newValue) {
        const valStr = getModelString(newValue);
        const parsedDate = new Date(valStr.replace(' ', 'T'));
        currentDate.value = parsedDate;
        
        if (props.enableTime) {
            selectedHour.value = parsedDate.getHours();
            selectedMinute.value = parsedDate.getMinutes();
            selectedDate.value = valStr.substring(0, 10);
        } else {
            selectedDate.value = valStr;
        }
      }
    }, { immediate: true });

    // Event listeners
    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
    });

    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      isOpen,
      weekDays,
      displayValue,
      currentYear,
      currentMonth,
      monthsList,
      yearList,
      handleMonthChange,
      handleYearChange,
      calendarDays,
      toggleCalendar,
      closeCalendar,
      selectDate,
      clearDate,
      selectToday,
      previousMonth,
      nextMonth,
      isSelected,
      isToday,
      isDateDisabled,
      selectedHour,
      selectedMinute
    };
  }
};
</script>

<style scoped>
.date-picker-container {
  position: relative;
  width: 100%;
}

.date-input-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 44px;
}

.date-input-trigger:hover {
  border-color: #D1D5DB;
}

.date-input-trigger.focused {
  border-color: var(--primary-border, #F93E3E);
  box-shadow: 0 0 0 3px var(--secondary-color, rgba(249, 62, 62, 0.15));
}

.date-input-trigger.has-value .selected-date {
  color: #111827;
}

.selected-date {
  font-size: 12px;
  color: #6B7280;
  font-weight: 400;
  white-space: nowrap;
}

.calendar-icon {
  color: #6B7280;
  flex-shrink: 0;
}

.calendar-popup {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 320px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  z-index: 10000;
  padding: 16px;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

.calendar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.nav-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.nav-button:hover {
  border-color: #D1D5DB;
  background: #F9FAFB;
}

.month-year-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.month-select,
.year-select {
  border: none;
  background: transparent;
  font-size: 15px;
  font-weight: 600;
  color: #111827;
  cursor: pointer;
  outline: none;
  padding: 4px;
}

.month-select:hover,
.year-select:hover {
  background: #F3F4F6;
  border-radius: 4px;
}

.year-select {
  font-weight: 500;
  color: #4B5563;
}

.week-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-bottom: 8px;
}

.week-day {
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  color: #6B7280;
  padding: 8px 4px;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
}

.calendar-day {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  -webkit-user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.calendar-day.current-month {
  color: #111827;
}

@media (hover: hover) and (pointer: fine) {
  .calendar-day.current-month:not(.selected):hover {
    background: #F3F4F6;
    color: #111827;
  }
}

.calendar-day.selected {
  background: var(--primary-color, #F93E3E);
  color: white;
  font-weight: 500;
}

.calendar-day.today:not(.selected) {
  background: transparent;
  color: var(--primary-color, #F93E3E);
  font-weight: 600;
  border: 2px solid var(--primary-border, #F93E3E);
}

.calendar-day.today.selected {
  background: var(--primary-color, #F93E3E);
  color: white;
  font-weight: 500;
  border: none;
}

.calendar-day.disabled {
  color: #D1D5DB;
  cursor: not-allowed;
}

.calendar-actions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #F3F4F6;
}

.action-btn {
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  background: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn.today {
  background: var(--primary-color, #F93E3E);
  color: white;
  border-color: var(--primary-border, #F93E3E);
}

.action-btn.today:hover {
  filter: brightness(0.9);
}

.action-btn.clear {
  color: #6B7280;
}

.action-btn.clear:hover {
  background: var(--secondary-color, #FEE9E9);
  color: var(--primary-color, #F93E3E);
  border-color: var(--secondary-color, #FEE9E9);
}

.action-btn.apply {
  background: var(--primary-color, #F93E3E);
  color: white;
  border-color: var(--primary-border, #F93E3E);
}

.action-btn.apply:hover {
  filter: brightness(0.9);
}

.time-selectors {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #F3F4F6;
}

.time-selector-group {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.time-selector-group label {
  font-size: 11px;
  color: #6B7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.time-select {
  padding: 6px 8px;
  border: 1px solid #E5E7EB;
  border-radius: 6px;
  background: #F9FAFB;
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  outline: none;
  cursor: pointer;
  min-width: 60px;
  text-align: center;
}

.time-select:hover {
  border-color: #D1D5DB;
}

.time-select:focus {
  border-color: var(--primary-color, #F93E3E);
  box-shadow: 0 0 0 2px var(--secondary-color, rgba(249, 62, 62, 0.15));
}

.time-separator {
  font-weight: 600;
  color: #4B5563;
  margin-top: 16px;
}

.calendar-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  backdrop-filter: blur(2px);
}

/* Animations */
.calendar-slide-enter-active,
.calendar-slide-leave-active {
  transition: all 0.3s ease;
}

.calendar-slide-enter-from,
.calendar-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 480px) {
  .calendar-popup {
    width: 90%;
    max-width: 280px;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
  
  .calendar-day {
    height: 32px;
    font-size: 13px;
  }
}
</style>